// =============================================================================
// handoff-briefing.ts — Contrato Zod do parecer da IA no handoff
// (`ai_handoff_briefings.assessment`, F37-S01, doc 28 §3 D4).
//
// DESVIO DE ESCOPO DELIBERADO (registrado no PR/slot F37-S01): o slot
// original listava este contrato sob `packages/shared-types/src/**`, mas
// shared-types é documentado como "apenas tipos puros, sem dependência de
// runtime — schemas Zod ficam em @elemento/shared-schemas" (ver cabeçalho
// de packages/shared-types/src/index.ts). Todo o resto do monorepo segue
// essa convenção (ex.: CALL_TECHNICAL_STATES, SALES_OUTCOME_CODES). Os
// literais de domínio (QualificationStepKey, HandoffOutcome, CustomerTone,
// AssessmentConfidenceLevel, BriefingStatus) foram publicados em
// `@elemento/shared-types` (tipos puros, consumidos também pelo Drizzle e
// pelo futuro frontend) — este arquivo espelha os MESMOS valores em domínios
// Zod locais, mesmo padrão de duplicação intencional já usado em
// `telephonyCalls.ts` (CALL_DIRECTIONS) para não criar acoplamento de
// runtime entre os dois pacotes de contrato. Mudar um valor exige atualizar
// os dois lugares — documentado aqui e em qualification.ts.
//
// LGPD (doc 17, doc 28 §3 D6): este schema valida o OUTPUT ESTRUTURADO do
// LLM — nunca deve carregar CPF, telefone ou renda. Os limites de tamanho
// abaixo (summary ≤280, recommendedAction ≤140, attentionPoints ≤4×100,
// customerTone ≤2) são o contrato de borda que impede o LLM de devolver
// texto livre sem teto — validado em `/internal` (F37-S03/S04).
// =============================================================================
import { z } from 'zod';
/** Espelha HANDOFF_OUTCOMES de `@elemento/shared-types` (doc 28 §3 D4). */
export const HANDOFF_OUTCOMES = [
    'qualified',
    'partial',
    'customer_requested_human',
    'out_of_scope',
    'technical_failure',
];
/** Espelha CUSTOMER_TONES de `@elemento/shared-types` (doc 28 §3 D4). */
export const CUSTOMER_TONES = [
    'interested',
    'hesitant',
    'in_a_hurry',
    'frustrated',
    'confused',
];
/** Espelha ASSESSMENT_CONFIDENCE_LEVELS de `@elemento/shared-types`. */
export const ASSESSMENT_CONFIDENCE_LEVELS = ['high', 'medium', 'low'];
/**
 * Parecer estruturado da IA sobre um handoff (doc 28 §3 D4). Gerado pelo
 * LangGraph com `response_format` JSON + validação Pydantic espelhada
 * (F37-S03); validado de novo na borda `/internal` do Node (defesa em
 * profundidade contra LLM que ignora o schema).
 */
export const HandoffAssessmentSchema = z.object({
    /** Resumo em 1-2 frases do que aconteceu na conversa. */
    summary: z.string().min(1).max(280),
    /** Desfecho avaliado do handoff — domínio fechado (doc 28 §3 D4). */
    outcome: z.enum(HANDOFF_OUTCOMES),
    /** Sugestão de próxima ação para o humano. */
    recommendedAction: z.string().min(1).max(140),
    /**
     * Pontos de atenção levantados pela IA (ex.: "tem dívida ativa"). No
     * máximo 4 itens — nunca um dump de observações sem curadoria.
     */
    attentionPoints: z.array(z.string().min(1).max(100)).max(4).default([]),
    /**
     * Tom percebido do cliente. No máximo 2 itens — o LLM escolhe os mais
     * relevantes, não uma lista exaustiva.
     */
    customerTone: z.array(z.enum(CUSTOMER_TONES)).max(2).default([]),
    /** Confiança declarada pelo próprio LLM no parecer. */
    confidence: z.enum(ASSESSMENT_CONFIDENCE_LEVELS),
});
//# sourceMappingURL=handoff-briefing.js.map